import axios from "axios";
import { toast } from "react-toastify";
import { handleApiError, isAuthenticationError } from "../utils/errorHandler";
import requestSigner from "../functions/requestSigning";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3006";

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 seconds
  withCredentials: true, // Include credentials in requests
});

/**
 * Retry configuration
 */
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

/**
 * Retry-able error codes
 */
const RETRYABLE_ERROR_CODES = [
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_NETWORK',
];

/**
 * Retry-able status codes
 */
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if error is retry-able
 */
const isRetryableError = (error) => {
  if (!error.config) return false;

  // Don't retry if max retries reached
  const retryCount = error.config.__retryCount || 0;
  if (retryCount >= MAX_RETRIES) return false;

  // Check for retry-able error codes
  if (error.code && RETRYABLE_ERROR_CODES.includes(error.code)) {
    return true;
  }

  // Check for retry-able status codes
  if (error.response?.status && RETRYABLE_STATUS_CODES.includes(error.response.status)) {
    return true;
  }

  return false;
};

/**
 * Request interceptor - Add auth token and request signature to requests
 */
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: Date.now() };

    // Add request signature for protected endpoints
    const method = config.method.toUpperCase();
    const path = config.url.startsWith('http')
      ? new URL(config.url).pathname
      : config.url;

    // Check if this endpoint requires signature
    if (requestSigner.requiresSignature(path)) {
      console.log('🔐 Adding signature for:', method, path);
      const signatureHeaders = requestSigner.generateSignatureHeaders(
        method,
        path,
        config.data
      );

      // Add signature headers to request
      config.headers = {
        ...config.headers,
        ...signatureHeaders
      };

      console.log('✅ Signature headers added:', signatureHeaders);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle responses and errors globally
 */
apiClient.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = Date.now() - response.config.metadata.startTime;

    // Log slow requests in development
    if (process.env.NODE_ENV === 'development' && duration > 3000) {
      console.warn(`Slow request detected: ${response.config.url} took ${duration}ms`);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Retry logic for retry-able errors
    if (isRetryableError(error)) {
      originalRequest.__retryCount = (originalRequest.__retryCount || 0) + 1;

      const retryDelay = RETRY_DELAY * originalRequest.__retryCount;
      console.log(`Retrying request (attempt ${originalRequest.__retryCount}/${MAX_RETRIES}) after ${retryDelay}ms...`);

      await sleep(retryDelay);
      return apiClient(originalRequest);
    }

    // Handle different error scenarios
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - handle authentication errors
          // Only logout if we have a valid token but it was rejected
          const currentToken = localStorage.getItem("authToken");

          if (currentToken && !originalRequest._retry) {
            originalRequest._retry = true;

            console.log('❌ 401 Unauthorized - Token was rejected by server');
            console.log('Endpoint:', originalRequest.url);
            console.log('Token exists:', !!currentToken);

            // Clear auth data
            localStorage.removeItem("authToken");
            localStorage.removeItem("representativeUser");
            localStorage.removeItem("selectedProgramme");
            localStorage.removeItem("programmes");

            // Show toast notification
            toast.error("Your session has expired. Please sign in again.", {
              toastId: 'auth-error',
              autoClose: 3000,
            });

            // Redirect to login after a brief delay
            setTimeout(() => {
              window.location.href = "/login";
            }, 1000);
          } else if (!currentToken) {
            // No token exists, redirect to login without toast
            console.log('❌ 401 Unauthorized - No token found, redirecting to login');
            window.location.href = "/login";
          }
          break;

        case 403:
          // Forbidden - access denied
          handleApiError(error, {
            fallbackMessage: "You do not have permission to perform this action.",
            toastId: 'forbidden-error',
          });
          break;

        case 404:
          // Not found
          handleApiError(error, {
            fallbackMessage: "The requested resource was not found.",
            showToast: false, // Let individual components handle 404s
          });
          break;

        case 422:
          // Validation error - don't show global toast
          handleApiError(error, { showToast: false });
          break;

        case 429:
          // Too many requests
          toast.warning("Too many requests. Please slow down and try again.", {
            toastId: 'rate-limit-error',
            autoClose: 5000,
          });
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          handleApiError(error, {
            fallbackMessage: "A server error occurred. Please try again later.",
            toastId: 'server-error',
          });
          break;

        default:
          // Other errors
          if (status >= 400) {
            handleApiError(error);
          }
      }
    } else if (error.request) {
      // Request was made but no response received (network error)
      handleApiError(error, {
        fallbackMessage: "Unable to connect to the server. Please check your internet connection.",
        toastId: 'network-error',
      });
    } else {
      // Something else happened
      handleApiError(error);
    }

    // Enhance error object with additional information
    error.isAxiosError = true;
    error.timestamp = Date.now();

    return Promise.reject(error);
  }
);

/**
 * Helper function to make GET requests with error handling
 */
export const get = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to make POST requests with error handling
 */
export const post = async (url, data, config = {}) => {
  try {
    console.log('[API POST] Request:', { url, data });
    const response = await apiClient.post(url, data, config);
    console.log('[API POST] Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API POST] Error:', error);
    console.error('[API POST] Error response:', error.response?.data);
    console.error('[API POST] Error status:', error.response?.status);
    console.error('[API POST] Request data:', data);
    throw error;
  }
};

/**
 * Helper function to make PUT requests with error handling
 */
export const put = async (url, data, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to make PATCH requests with error handling
 */
export const patch = async (url, data, config = {}) => {
  try {
    const response = await apiClient.patch(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to make DELETE requests with error handling
 */
export const del = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default apiClient;